import { FormGroup, InputLabel, makeStyles, MenuItem, Select } from "@material-ui/core";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import LinksTitle, { LinksButton } from "./LinksTitle";
import { useCustomerList, useSelectedUserProfile, useUserProfile } from "./store/UserProfileState";
import { useBoolean } from "./hooks/useBoolean";
import { useRecoilValue } from "recoil";
import { noUseWorkspaceState } from "./store/NoUseWorkspaceState";
import { Loading } from "./parts/Loading";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  label: {
    marginTop: 7,
    marginRight: 7,
    width: 135,
    height: 21
  },
  select: {
    minWidth: 266
  },
  formInputArea: {
    marginBottom: "10px"
  },
  margin: {
    marginTop: 10
  },
  userName: {
    color: '#000000'
  },
  outlined: {
    padding: 10
  }
  
}));

function UserGroupSelect({customer, usergroup, handleUsergroupChange}) {
  const {userProfile} = useSelectedUserProfile(customer);
  const myUsergroup = userProfile.usergroups?.some(ug => ug.usergroup_id === usergroup) ? usergroup : '';

  const classes = useStyles();
  // const {t} = useTranslation();

  return (
    <Select
      className={classes.select}
      value={myUsergroup}
      onChange={handleUsergroupChange}
      variant="outlined"
      classes={{outlined: classes.outlined}}
    >
      {userProfile.usergroups?.map(ug => {
        return (<MenuItem key={ug.usergroup_id} value={ug.usergroup_id}>{`${ug.usergroup_name} - ${ug.contract_name}`}</MenuItem>)
      })}
    </Select>
  );
}

function CustomerSelect({usergroup, setUsergroup, handleUsergroupChange, setButtonDisabled}) {
  const [customer, setCustomer] = useState('');
  const {customerList} = useCustomerList();

  useEffect(() => {
    setButtonDisabled(!customer || !usergroup);
  }, [customer, usergroup, setButtonDisabled]);
 
  const handleCustomerChange = (event) => {
    setCustomer(event.target.value);
    setUsergroup('');
  };

  const classes = useStyles();
  const {t} = useTranslation();

  return (
    <>
      <FormGroup className={classes.margin}>
        <InputLabel className={classes.label}>{t('Customer')}</InputLabel>
        <Select
          className={classes.select}
          value={customer}
          onChange={handleCustomerChange}
          variant="outlined"
          classes={{outlined: classes.outlined}}
        >
          {customerList.map(customer => {
            return (<MenuItem key={customer.customer_code} value={customer.customer_code}>{`${customer.customer_code} / ${customer.customer_name}`}</MenuItem>)
          })}
        </Select>
      </FormGroup>
      <FormGroup className={classes.margin}>
        <InputLabel className={classes.label}>{t('User Group')}</InputLabel>
        <Suspense fallback={<Loading />}>
          <UserGroupSelect customer={customer} usergroup={usergroup} handleUsergroupChange={handleUsergroupChange} />
        </Suspense>
      </FormGroup>
    </>
  );
}

function UserProfileSelect() {

  const [hidden, hide, show] = useBoolean(false);
  const [error, setError] = useState(null);
  const {userProfile, setUserProfile} = useUserProfile();
  const noUseWorkspace = useRecoilValue(noUseWorkspaceState);
  const [usergroup, setUsergroup] = useState(() => {
    if (userProfile.usergroups && userProfile.usergroups.length > 0) {
      return userProfile.usergroups[0].usergroup_id;
    }
    return '';
  });
  const [workspace, setWorkSpace] = useState(() => {
    if (noUseWorkspace) {
      return '';
    }
    if (userProfile.default_workspace_id) {
      return userProfile.default_workspace_id;
    }
    if (userProfile.workspaces && userProfile.workspaces.length > 0) {
      return userProfile.workspaces[0].workspace_id;
    }
    return '';
  });
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const isWniRole = userProfile.is_wni === true;

  const classes = useStyles();

  const history = useHistory();
  const {t} = useTranslation();

  const handleUsergroupChange = (event) => {
    setUsergroup(event.target.value);
  };

  const handleWorkspaceChange = (event) => {
    setWorkSpace(event.target.value);
  };

  const handleClick = (evnet) => {
    gotoMap(usergroup, workspace);
  }

  const gotoMap = useCallback(async (usergroupId, workspaceId) => {
    hide();
    try {
      const _usergroupId = usergroupId !== '' ? usergroupId : undefined;
      const _workspaceId = !isWniRole && workspaceId !== '' ? workspaceId : undefined;
      await setUserProfile(_usergroupId, _workspaceId);
      const to = {pathname: '/'};
      history.replace(to);
    } catch (e) {
      setError(e);
    } finally {
      show();
    }
  }, [hide, isWniRole, setUserProfile, history, setError, show]);

  useEffect(() => {
    if (hidden) {
      return;
    }
    const usergroups = userProfile.usergroups;
    const workspaces = userProfile.workspaces;
    if (!isWniRole && usergroups.length <= 1) {
      const usergroupId = usergroups.length === 1 ? usergroups[0].usergroup_id : '';
      if (noUseWorkspace || !workspaces || workspaces.length === 0) {
        gotoMap(usergroupId, '');
      } else if (workspaces.length === 1) {
        gotoMap(usergroupId, workspaces[0].workspace_id);
      }
    }
  }, [hidden, userProfile, noUseWorkspace, gotoMap, isWniRole]);

  useEffect(() => {
    // エラーがセットされていたら例外をthrowする
    if (error) {
      throw error;
    }
  }, [error]);

  return (
    <>
    {!hidden &&
    <LinksTitle>
      <div className={classes.paper}>
        <form className={classes.paper}>
          <div className={classes.formInputArea}>
            <FormGroup className={classes.margin} >
              <InputLabel className={clsx(classes.label, classes.userName)}>{userProfile.login_id}</InputLabel>
            </FormGroup>
            {isWniRole ?
              <CustomerSelect usergroup={usergroup} setUsergroup={setUsergroup} handleUsergroupChange={handleUsergroupChange} setButtonDisabled={setButtonDisabled}/>
            :
              <FormGroup className={classes.margin} >
                <InputLabel className={classes.label}>{t('User Group')} </InputLabel>
                <Select
                  className={classes.select}
                  value={usergroup}
                  onChange={handleUsergroupChange}
                  variant="outlined"
                  classes={{outlined: classes.outlined}}
                >
                  { userProfile.usergroups.map(ug => {
                    return (<MenuItem key={ug.usergroup_id} value={ug.usergroup_id}>{`${ug.usergroup_name} - ${ug.contract_name}`}</MenuItem>)
                  })}
                </Select>
                </FormGroup>
            }
            {!isWniRole && !noUseWorkspace && userProfile.workspaces && userProfile.workspaces.length > 0 &&
            <FormGroup className={classes.margin} >
              <InputLabel className={classes.label}>{t('Work Space')} </InputLabel>
              <Select
                className={classes.select}
                value={workspace}
                onChange={handleWorkspaceChange}
                variant='outlined'
                classes={{outlined: classes.outlined}}
              >
                { userProfile.workspaces.map(ws => {
                  return (<MenuItem key={ws.workspace_id} value={ws.workspace_id}>{ws.name}</MenuItem>)
                })}
              </Select>
            </FormGroup>
            }
          </div>
          <LinksButton
            type="button"
            variant="contained"
            onClick={handleClick}
            style={{ width: '100%', marginTop: 28 }}
            disabled={buttonDisabled}
          >
            {t("Go")}
          </LinksButton>
        </form>
      </div>
    </LinksTitle>
    }
    </>
  );
}

export default UserProfileSelect;
